import {defineToolRegistryManifest} from '../../types/base-tool-types';
import {z} from 'zod';
import dedent from 'dedent';

const toolName = 'read-page' as const;

const argumentZod = z.object({
  startLine: z
    .number()
    .int()
    .optional()
    .describe(
      'Starting line number (1-indexed). Supports negative numbers to count from the end (e.g. -50 = last 50 lines). Defaults to 1.'
    ),
  endLine: z
    .number()
    .int()
    .optional()
    .describe(
      'Ending line number (1-indexed, inclusive). Supports negative numbers (e.g. -1 = last line). Defaults to last line.s'
    ),
});

export const readPageTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Read page',
  prettyStatus: {
    executing: 'Reading page...',
    executed: 'Read page',
    failed: 'Failed to read page',
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Reads the full HTML and CSS of the current page. Returns data-w-id attributes for use with render and other element tools. Use startLine/endLine to paginate large pages.

      Use this tool to read the current page — never \`read-file\` with a page name; pages are not files.

      Reuse this result only while no page-mutating tool or external action has changed the page. After a successful mutation or STALE_CONTEXT response, re-read before using element IDs or markup from an earlier result.

      Call without parameters first to see totalLines, then slice as needed using startLine/endLine.

       **Component instances** (\`<wf-component>\` elements) in read-page output are opaque references — their internal structure is managed by the component definition. Re-read when you need to locate a specific element for a new edit. Do not re-read solely to validate component internals — if a render call succeeds, the component is correctly placed.

      **Editing components (only applies when \`<wf-component>\` tags appear in the output):** Elements inside \`<wf-component>\` tags CANNOT be edited with set-element-text-content or any element-level tool. Components are edited through their props only:
      - If the user asks to change text inside a component, check the component's \`prop-*\` attributes for a matching prop.
      - If a matching prop exists, use the render tool with MODIFY mode: \`<wf-component data-w-id="..." prop-name="new value">\`.
      - If NO matching prop exists, tell the user the content cannot be edited — it is inside a component that does not expose a prop for it.
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  permission: [{resource: 'SitePage', action: 'editContent'}],
  confirmBeforeExecuting: false,
  isConcurrencySafe: () => false,
  isReadOnly: () => true,
  timeout: 30000,
  compaction: {
    prompt:
      'Summarize the page HTML structure. Preserve element data-w-ids, CSS class names, heading content, key text, component types, and structural hierarchy. Remove raw HTML tags and repetitive markup.',
    staleAfterTurns: 3,
    supersededOnRecall: true,
  },
});
